import { MouseEvent, useState } from 'react';
import { NavLink, useHistory } from 'react-router-dom';

import { Box, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { Avatar, Menu as DsMenu } from '@natter-co/design-system';
import { Button, UserAvatar, Divider } from 'common/components';
import { useInheritedVisualRefresh } from 'common/theme';
import { Account } from 'domain/Account';
import { ChevronDownIcon, LogoutIcon, SettingsAccountIcon } from 'icons';
import { AppRoutes } from 'modules/router';

type Props = {
  account: Account;
  onLogout(): void;
};

export const CurrentUser = ({ account, onLogout }: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { displayName, email, avatarLink } = account;
  const visualRefresh = useInheritedVisualRefresh();
  const history = useHistory();

  const handleMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box data-testid="current-user">
      <Stack flexDirection="row" alignItems="center" gap={1}>
        <Button
          variant="text"
          onClick={handleMenu}
          startIcon={
            visualRefresh ? (
              // Decorative: the name is printed in the button beside it. The span keeps the Avatar
              // from being the startIcon slot's first child, which MUI forces to font-size 20px.
              <span className="inline-flex">
                <Avatar name={displayName} src={avatarLink} size={24} decorative />
              </span>
            ) : (
              <UserAvatar name={displayName} avatarLink={avatarLink} dimensions={24} />
            )
          }
          endIcon=<ChevronDownIcon fontSize="small" color="action" />
          aria-label="account of current user"
          aria-controls="menu-appbar"
          aria-haspopup="true"
          disableRipple
          sx={{ mr: -1, px: 1 }}
        >
          <Typography color="text.secondary">{displayName}</Typography>
        </Button>
      </Stack>

      {visualRefresh ? (
        /*
         * The DS 2.0 menu, which `common/components/Menu` cannot reach from here: this menu was always
         * hand-built on MUI's `Menu`, with the name and email as a header above the rows. That header is
         * the DS menu's `header` slot — not drawn in Figma yet, see `menuHeaderRecipe`.
         *
         * Account settings navigates through the router rather than rendering a `NavLink`, because a DS
         * row is an item, not a link.
         */
        <DsMenu
          open={Boolean(anchorEl)}
          anchor={anchorEl}
          onOpenChange={(open) => !open && handleClose()}
          side="bottom"
          align="end"
          className="w-[240px]"
          header={{ title: displayName, description: email }}
          options={[
            {
              id: 'account-settings',
              label: 'Account settings',
              icon: <SettingsAccountIcon fontSize="small" />,
              onSelect: () => history.push(AppRoutes.Account),
            },
            { id: 'log-out', label: 'Log out', icon: <LogoutIcon fontSize="small" />, onSelect: onLogout },
          ]}
        />
      ) : (
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          data-testid="current-user-menu"
          MenuListProps={{ sx: { px: 0 } }}
        >
          <Stack gap={0.5} p={1.5} pt={1} width={240} aria-label="User information">
            <Typography variant="natter-text-md" fontWeight={500}>
              {displayName}
            </Typography>
            <Typography variant="natter-text-xs" color="text.secondary">
              {email}
            </Typography>
          </Stack>
          <Divider />
          <Stack mt={0.75}>
            <MenuItem component={NavLink} to={AppRoutes.Account} onClick={handleClose}>
              <ListItemIcon>
                <SettingsAccountIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Account settings</ListItemText>
            </MenuItem>
            <MenuItem onClick={onLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Log out</ListItemText>
            </MenuItem>
          </Stack>
        </Menu>
      )}
    </Box>
  );
};
